import type { KgInsight } from '@api/api/kg-insights';
import {
  POSTHOG_TRACKING_SOURCES,
  PosthogTrackingSource,
} from '@terminal/services/tracking/posthog/constants';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { DownloadButton } from '@terminal/views/insight-feed/components/download-button/DownloadButton';
import { InsightCopyBtn } from '@terminal/widgets/insight-widget/components/insight-copy-btn/InsightCopyBtn';
import { useInsightCopy } from '@terminal/widgets/insight-widget/components/insight-copy-btn/useInsightCopy';
import React, { type RefObject } from 'react';
import { CompanyCatalystBadge } from '../company-catalyst-badge/CompanyCatalystBadge';
import { ThematicCatalystBadge } from '../thematic-catalyst-badge/ThematicCatalystBadge';
import { KgInsightHeaderButton } from './KgInsightHeaderButton';
import styles from './KgInsightPreview.module.scss';

export type KgInsightHeaderActionProps = {
  subject: KgInsight['subject'];
  source?: PosthogTrackingSource;
  onDownload?: () => void;
  contentWrapperRef?: RefObject<HTMLDivElement | null>;
  expandAccordions?: (expand: boolean) => void;
};

export const KgInsightHeaderAction = ({
  subject,
  source = POSTHOG_TRACKING_SOURCES.CATALYST_INSIGHT_DRAWER,
  onDownload,
  contentWrapperRef,
  expandAccordions,
}: KgInsightHeaderActionProps) => {
  const catalystBadgeByMode: Partial<
    Record<KgInsight['subject']['type'], React.ReactNode>
  > = {
    entity: <CompanyCatalystBadge entityTag={subject.id} source={source} />,
    theme: <ThematicCatalystBadge themeId={subject.id} source={source} />,
    geo: <CompanyCatalystBadge entityTag={subject.id} source={source} />,
  };

  const isCopyPasteInsightsEnabled = useFlags(state =>
    state.getFeatureFlagState('REASON-2424-insights-page-copy')
  );

  const { isCopying, handleCopyInsight } = useInsightCopy({
    contentWrapperRef,
    expandAccordions,
  });

  return (
    <div className={styles.headerActionWrapper}>
      <div className={styles.headerActionLeft}>
        {catalystBadgeByMode[subject.type]}
        {onDownload && (
          <DownloadButton
            onClick={onDownload}
            className={styles.downloadButton}
          />
        )}
        {isCopyPasteInsightsEnabled && contentWrapperRef && (
          <InsightCopyBtn
            onClick={handleCopyInsight}
            className={styles.copyButton}
            isLoading={isCopying}
            disabled={isCopying}
          />
        )}
      </div>
      <KgInsightHeaderButton subject={subject} />
    </div>
  );
};
